import { __read } from "tslib";
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { ItemUtils } from '@marvelapp/design-creator';
import { usePrevious } from '@marvelapp/hooks';
import { Box, Flex, IconButton, Input, Text } from '@marvelapp/ui';
import Highlighter from '../Highlighter';
import { SelectionOption } from '../SelectionList/SelectionOption';
import { LayerIcon } from './Icon';
import { CaretDownIcon, LayerClippedArrowIcon } from './icons';
import { getDefaultLayerLabel, isSymbolLayer, scrollToLayer } from './utils';
function Layer(_a) {
    var _b;
    var filterText = _a.filterText, item = _a.item, isSelected = _a.isSelected, onMouseDown = _a.onMouseDown, onMouseUp = _a.onMouseUp, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, onRename = _a.onRename, isCollapsed = _a.isCollapsed, onCollapse = _a.onCollapse, isClipped = _a.isClipped;
    var _c = __read(useState(null), 2), stagedLayerName = _c[0], setStagedLayerName = _c[1];
    var _d = __read(useState(false), 2), isFocus = _d[0], setIsFocus = _d[1];
    var inputRef = useRef(null);
    var selectionRef = useRef(null);
    var previousFilterText = usePrevious(filterText);
    var itemDisplayName = ItemUtils.getName(item) || getDefaultLayerLabel(item);
    // Scroll selected layer into view if filter is reset
    useLayoutEffect(function () {
        if (previousFilterText && !filterText && isSelected) {
            scrollToLayer(selectionRef.current);
        }
    }, [previousFilterText, filterText, isSelected]);
    // Scroll selected layer into view when it becomes selected
    useLayoutEffect(function () {
        if (isSelected) {
            scrollToLayer(selectionRef.current);
        }
    }, [isSelected]);
    useEffect(function () {
        if (isFocus) {
            inputRef.current.focus();
        }
    }, [isFocus]);
    var commitStagedLayerName = useCallback(function () {
        if (onRename) {
            onRename(item.id, stagedLayerName);
        }
    }, [item.id, onRename, stagedLayerName]);
    var handleOnFocus = useCallback(function (event) {
        event.target.select();
    }, []);
    var handleOnBlur = useCallback(function () {
        commitStagedLayerName();
        setIsFocus(false);
    }, [commitStagedLayerName]);
    var handleKeyDown = useCallback(function (event) {
        if (!onRename || !isFocus) {
            return;
        }
        if (event.key === 'Enter') {
            commitStagedLayerName();
            setIsFocus(false);
        }
        if (event.key === 'Escape') {
            setStagedLayerName(null);
            setIsFocus(false);
        }
    }, [commitStagedLayerName, isFocus, onRename]);
    var handleDoubleClick = useCallback(function () {
        // Only enable rename when editable
        if (onRename) {
            setIsFocus(true);
            setStagedLayerName(itemDisplayName);
        }
    }, [itemDisplayName, onRename]);
    var onLayerInputChange = useCallback(function (e) {
        setStagedLayerName(e.target.value);
    }, []);
    var handleOnMouseEnter = function (event) { return onMouseEnter(item.id, event); };
    var handleOnMouseLeave = function (event) { return onMouseLeave(item.id, event); };
    var handleOnMouseDown = function (event) { return onMouseDown(item.id, event); };
    var handleOnMouseUp = function (event) { return onMouseUp(item.id, event); };
    var hasChildren = !!((_b = item.children) === null || _b === void 0 ? void 0 : _b.length);
    var shouldShowCaret = hasChildren;
    var fontSize = '12px';
    var fontWeight = isSelected ? '500' : '400';
    var inputMargin = 4;
    var inputHeight = 30 - inputMargin * 2;
    return (React.createElement(SelectionOption, { "data-layerid": item.id, "data-selectedoption": isSelected, "data-testid": "layer-item", enableScrollIntoView: true, isSelected: isSelected, onDoubleClick: handleDoubleClick, onMouseDown: handleOnMouseDown, onMouseEnter: handleOnMouseEnter, onMouseLeave: handleOnMouseLeave, onMouseUp: handleOnMouseUp, pl: 1, ref: selectionRef, 
        // eslint-disable-next-line no-use-before-define
        styleOverrides: isSymbolLayer(item) && styleOverrides.symbol },
        shouldShowCaret ? (React.createElement(IconButton, { bg: "transparent", "data-testid": "layer-item-toggle", hoverBg: "transparent", onClick: onCollapse, onMouseDown: function (e) { return e.stopPropagation(); } },
            React.createElement(Box, { transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0)' },
                React.createElement(CaretDownIcon, { color: isSelected ? 'white' : 'black', width: "8px" })))) : (React.createElement(Box, { height: 26, width: 26 })),
        React.createElement(Flex, { alignItems: "center" },
            isClipped && React.createElement(LayerClippedArrowIcon, { ml: 2 }),
            React.createElement(LayerIcon, { isCollapsed: isCollapsed, item: item })),
        React.createElement(Box, { flex: 1, minWidth: "1px" }, isFocus ? (React.createElement(Input, { bg: isFocus ? 'white' : 'transparent', border: "none", color: "black", "data-testid": "layer-name-input", fontSize: fontSize, fontWeight: fontWeight, height: inputHeight, kind: "custom", lineHeight: 1, onBlur: handleOnBlur, onChange: onLayerInputChange, onFocus: handleOnFocus, onKeyDown: handleKeyDown, pl: 1, ref: inputRef, value: stagedLayerName, width: 1 })) : (React.createElement(Text, { cursor: "default", "data-testid": "layer-name", fontSize: fontSize, fontWeight: fontWeight, ml: 1, truncated: true, userSelect: "none", width: 1 },
            React.createElement(Highlighter, { searchWords: [filterText], textToHighlight: itemDisplayName }))))));
}
export default observer(Layer);
var styleOverrides = {
    symbol: {
        default: {
            bg: 'transparent',
            color: 'purple',
            hoverBg: 'snowDark',
        },
        selected: {
            bg: 'purple',
            color: 'white',
            hoverBg: 'purple',
        },
    },
};
